/**
 * Pure helpers for the inline `<private />` directive.
 *
 * Marks a private (organization-tailored) company result with a small
 * inline pill — vector lock icon + "Private" label on a lilac/purple
 * wash, matching the right-rail citation sidenote private badge
 * (PLT-4548 / PLT-4602) and the MCP App card header. Replaces the
 * earlier 🔒 emoji marker (PLT-4548) and its follow-up `<lock />`
 * directive (PLT-4600), both of which preceded the company name.
 *
 * PLT-4617 changed the directive shape twice over:
 *
 *   - Renamed `<lock />` → `<private />`. The marker's job is to label
 *     a result as private, not to render a lock per se; the lock is the
 *     visual treatment, not the meaning. Renaming also gives a clean
 *     break from any prompt-cache that still teaches `<lock />`.
 *   - Repositioned the marker to AFTER the company name
 *     (`**Acme** <private />`, not `<lock /> **Acme**`). The chip now
 *     reads as a trailing label — same shape the right-rail sidenote
 *     uses, where the badge sits to the right of the company title.
 *
 * Internal lock-badge naming (file name, `LOCK_BADGE_HREF`,
 * `chat-private-lock-badge` CSS class, `InlineLockBadge` component) is
 * kept for continuity — the badge IS visually a lock, the rename is
 * only at the directive layer. Touching the URL prefix would force a
 * coordinated change in the routing override in `upright-chat-markdown`
 * for no functional gain.
 *
 * Mirrors the `tone-badge-markers.ts` and `citation-markers.ts`
 * patterns:
 *
 *   - lax tag parsing via `findTagsInText` — case-insensitive, tolerant
 *     of attribute quoting / whitespace, although the private tag
 *     itself takes no attributes.
 *   - separate from the remark plugin so unit tests can drive the
 *     splitter directly without pulling in ESM-only deps.
 *
 * Marker grammar:
 *
 *   `<private />`             — canonical, self-closing
 *   `<private></private>`     — paired form (empty body) also accepted
 *   `<PRIVATE />`             — case-insensitive
 *   `<private  / >`           — whitespace-tolerant
 *
 * Any inner text inside a paired form is ignored — the chip carries
 * its own "Private" label, so the surrounding prose no longer needs
 * a `(Private)` parenthetical.
 */
import { findTagsInText } from './html-tag-parser';

/** URL scheme used for the link nodes minted by `remarkLockBadges`. */
export const LOCK_BADGE_HREF_PREFIX = 'lock:';

/**
 * Singleton URL — the private chip has no payload, so every minted
 * link node carries the same href. Kept as a constant so the renderer
 * can compare with `===` rather than re-parsing the prefix each time.
 *
 * The literal `lock:` scheme is preserved from PLT-4600 even though
 * the directive name changed in PLT-4617 — see the file header for
 * the rationale.
 */
export const LOCK_BADGE_HREF = `${LOCK_BADGE_HREF_PREFIX}private`;

/** Mdast `text` node shape (subset). */
export interface MarkerTextNode {
  type: 'text';
  value: string;
}

/** Mdast `link` node minted to replace each marker (subset). */
export interface MarkerLinkNode {
  type: 'link';
  url: string;
  title: null;
  children: MarkerTextNode[];
}

export type MarkerSplitChild = MarkerTextNode | MarkerLinkNode;

/**
 * Split a text string on `<private />` directives and return the
 * resulting mdast children — text nodes for the spans between
 * directives, link nodes (URL = `lock:private`) for each one.
 *
 * Returns `null` when the string contains no `<private>` directive,
 * so the remark plugin can short-circuit without splicing the
 * parent's children.
 *
 * Pure function — no AST mutation, no `unist-util-visit` dependency.
 */
export function splitTextOnLockMarkers(text: string): MarkerSplitChild[] | null {
  const tags = findTagsInText(text, 'private');
  if (tags.length === 0) return null;

  const newChildren: MarkerSplitChild[] = [];
  let lastEnd = 0;
  for (const tag of tags) {
    if (tag.start > lastEnd) {
      let segment = text.slice(lastEnd, tag.start);
      // Strip a single trailing space character before the marker so
      // the chip hugs the company name on the left and visual breathing
      // room is supplied by the chip's own `margin-left`. Same trick
      // as `splitTextOnCitationMarkers`. PLT-4617: the chip now follows
      // the company name (vs. preceding it under PLT-4600), so the
      // trim point is still "the space directly before the marker" —
      // the splitter's contract did not change, only the typical input
      // shape did (`**Acme** <private />` instead of `<lock /> **Acme**`).
      if (segment.endsWith(' ')) segment = segment.slice(0, -1);
      if (segment.length > 0) {
        newChildren.push({ type: 'text', value: segment });
      }
    }
    // The `link` text content is unused by the chat UI's `a` override —
    // the renderer keys off the URL, not the children. Set it to a
    // placeholder so the link node is well-formed for any other consumer
    // that walks the AST.
    newChildren.push({
      type: 'link',
      url: LOCK_BADGE_HREF,
      title: null,
      children: [{ type: 'text', value: 'private' }],
    });
    lastEnd = tag.end;
  }
  if (lastEnd < text.length) {
    newChildren.push({ type: 'text', value: text.slice(lastEnd) });
  }
  return newChildren;
}

/**
 * If `href` is the lock-badge marker URL minted by `remarkLockBadges`,
 * return `true`. Otherwise `false`. The chat UI's `components.a`
 * override calls this on every link to decide whether to render an
 * `InlineLockBadge` or a regular anchor.
 */
export function isLockBadgeHref(href: string | undefined | null): boolean {
  if (typeof href !== 'string') return false;
  return href.startsWith(LOCK_BADGE_HREF_PREFIX);
}

/**
 * Build a `react-markdown` `urlTransform` that lets `lock:` URLs through
 * unchanged and delegates everything else to `defaultTransform`.
 *
 * Higher-order to keep `react-markdown` (ESM-only) out of this module's
 * import graph — callers compose it innermost-first with the citation
 * and tone-badge transforms.
 *
 * Without the passthrough, `react-markdown`'s `defaultUrlTransform`
 * strips any URL whose scheme isn't on its safe-list (replacing it with
 * `""`), so the `a`-component override would see an empty `href` and
 * could not dispatch to the `InlineLockBadge` renderer.
 */
export function makeLockAwareUrlTransform(defaultTransform: (url: string) => string): (url: string) => string {
  return url => {
    if (url.startsWith(LOCK_BADGE_HREF_PREFIX)) return url;
    return defaultTransform(url);
  };
}
